import { useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Radio } from "lucide-react";
import { toast } from "sonner";

export default function WaitlistForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", property_count: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/waitlist", form);
      setDone(true);
      toast.success("You're on the list");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="rounded-3xl bg-white border border-emerald-200 p-10 text-center" data-testid="waitlist-success">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
        <h3 className="mt-4 font-serif text-3xl text-[#0A1128]">You&apos;re on the list.</h3>
        <p className="mt-2 text-slate-600 text-sm max-w-md mx-auto">We&apos;ll reach out with early-access pilot details. Confirmation on its way to your inbox.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="waitlist-form" className="rounded-3xl bg-white border border-slate-200 p-8 md:p-10">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#F9F3E5] border border-[#D4AF37] flex items-center justify-center">
          <Radio className="w-5 h-5 text-[#0A1128]" />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Early Access</div>
          <h3 className="font-serif text-2xl text-[#0A1128]">Request the Verify™ device</h3>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div>
          <Label>Full name</Label>
          <Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="waitlist-name" />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} data-testid="waitlist-email" />
        </div>
        <div>
          <Label>Company / Brand</Label>
          <Input value={form.company} onChange={(e)=>setForm({...form, company: e.target.value})} data-testid="waitlist-company" />
        </div>
        <div>
          <Label>Portfolio size</Label>
          <Select value={form.property_count} onValueChange={(v)=>setForm({...form, property_count: v})}>
            <SelectTrigger data-testid="waitlist-portfolio"><SelectValue placeholder="Select a range" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 property</SelectItem>
              <SelectItem value="2-5">2 – 5</SelectItem>
              <SelectItem value="6-20">6 – 20</SelectItem>
              <SelectItem value="21-100">21 – 100</SelectItem>
              <SelectItem value="100+">100+</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-4">
        <Label>Anything we should know?</Label>
        <Textarea value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} data-testid="waitlist-notes"
          placeholder="Pilot timeline, region, existing platforms…" />
      </div>

      <Button type="submit" disabled={loading} data-testid="waitlist-submit"
        className="mt-6 rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white h-11 px-8">
        {loading ? "Joining…" : "Join the waitlist"}
      </Button>
      <p className="mt-3 text-xs text-slate-400">We&apos;ll only email you about Verify™ early access.</p>
    </form>
  );
}
